import { ObjectMap } from 'types';
import { getEnv, getAccessToken } from '../../helpers';

export const normalizeSlashUrl = (baseURL = '', url: string) => {
  const normalizedBaseUrl = baseURL.endsWith('/')
    ? baseURL.slice(0, baseURL.length - 1)
    : baseURL;
  const normalizedPath = url.startsWith('/') ? url.slice(1) : url;
  return `${normalizedBaseUrl}/${normalizedPath}`;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RequestBody = FormData | any;

// a wrapper class that allows
// replacement of axios lib used initially
class Api {
  baseURL: string;
  interceptors: Array<(request: RequestInit) => void>;
  defaultConfig: ObjectMap;

  constructor(url: string, defaultConfig = {}) {
    this.baseURL = url;
    this.interceptors = [];
    this.defaultConfig = defaultConfig;
  }

  addInterceptor(interceptFn: (request: RequestInit) => void) {
    this.interceptors.push(interceptFn);
  }

  appendParamsToURL(url: string, params: ObjectMap = {}) {
    if (params) {
      const paramsArr = [];
      for (const k of Object.keys(params)) {
        if (params[k] !== null && params[k] !== undefined) {
          paramsArr.push(`${k}=${encodeURIComponent(params[k])}`);
        }
      }
      if (paramsArr.length > 0) url = `${url}?${paramsArr.join('&')}`;
    }
    return url;
  }

  async request(
    url: string,
    body: RequestBody,
    method: string,
    config: ObjectMap = {},
  ) {
    let headers = config.headers || {};
    url = this.appendParamsToURL(url, config.params);

    if (body) {
      if (typeof body !== 'string') {
        if ('append' in body) {
          // TODO:
          // dealing with FormData
          // should we set Content-Type here?
        } else {
          body = JSON.stringify(body);
          headers = {
            'Content-Type': 'application/json',
            ...headers,
          };
        }
      }
    }

    const requestObj: RequestInit = {
      ...this.defaultConfig,
      method: method,
      headers: new Headers(headers),
      body,
    };

    this.interceptors.forEach((fn) => fn(requestObj));
    const res = await fetch(
      url.startsWith('http') ? url : normalizeSlashUrl(this.baseURL, url),
      requestObj,
    );
    if (res.status >= 400) {
      // sync the original axios reject a promise
      // on error, throw Error here to emulate it
      return Promise.reject(await res.json());
    }

    // get headers obj to return
    // it's used by cart
    const respHeaders: Record<string, string> = {};
    res.headers.forEach((val, key) => (respHeaders[key] = val));
    const data = await res.json();
    return { data, headers: respHeaders };
  }

  async get(url: string, config: ObjectMap = {}) {
    return await this.request(url, null, 'GET', config);
  }

  async post(url: string, body: RequestBody, config: ObjectMap = {}) {
    return await this.request(url, body, 'POST', config);
  }

  async patch(url: string, body: RequestBody, config: ObjectMap = {}) {
    return await this.request(url, body, 'PATCH', config);
  }

  async put(url: string, body: RequestBody, config: ObjectMap = {}) {
    return await this.request(url, body, 'PUT', config);
  }

  async delete(url: string, config: ObjectMap = {}) {
    return await this.request(url, null, 'DELETE', config);
  }
}

export const accessTokenInterceptor = (request: RequestInit) => {
  // some api may get call on the server-side
  if (typeof window === 'undefined') return;

  const accessToken = getAccessToken();
  if (accessToken) {
    (request.headers as Headers).set('Authorization', `Bearer ${accessToken}`);
  }
};

export const magentoApi = new Api(getEnv('BASE_EXTRA_SERVICE_URL') as string);

export const serverApi = new Api('/api');

export const searchApi = new Api(
  typeof window === 'undefined' && (getEnv('INTERNAL_SEARCH_URL') as string)
    ? (getEnv('INTERNAL_SEARCH_URL') as string)
    : (getEnv('BASE_SEARCH_URL') as string),
);

export const orderApi = new Api(getEnv('BASE_COV_URL') as string);

export const wordpressApi = new Api(getEnv('BASE_WORDPRESS_URL') as string);

export const ppmApi = new Api(getEnv('BASE_PPM_URL') as string);

export const cosmicApi = new Api('https://api.cosmicjs.com/v1/');

export const publicSettingApi = new Api(
  getEnv('BASE_PUBLIC_SETTING_API') as string,
);
